import { createClient } from 'payload-rest-client';
import { Config } from '@/types';

import { defaultDesignForTenant, isOrkhanTenant } from '@/lib/tenant';

type Locale = 'en';

export type CollectionType<T extends keyof Config['collections']> = Config['collections'][T];

type TenantReader = () => Promise<string | null>;
type DesignOverrideReader = () => Promise<string | null>;

/**
 * Registered from the server runtime only. This file is imported by client
 * components, so it must not import next/headers itself.
 */
// Instrumentation and SSR are separate module graphs in production. Share the
// reader function across those graphs, never the value of a particular request.
const tenantReaderKey = Symbol.for('raisonne.api.requestTenantReader');
const designOverrideReaderKey = Symbol.for('raisonne.api.requestDesignOverrideReader');
const runtime = globalThis as typeof globalThis & {
  [tenantReaderKey]?: TenantReader;
  [designOverrideReaderKey]?: DesignOverrideReader;
};

export function registerTenantReader(reader: TenantReader) {
  if (typeof window === 'undefined') runtime[tenantReaderKey] = reader;
}

export function registerDesignOverrideReader(reader: DesignOverrideReader) {
  if (typeof window === 'undefined') runtime[designOverrideReaderKey] = reader;
}

export async function getRequestTenant(): Promise<string | null> {
  if (typeof window !== 'undefined') return null;
  const reader = runtime[tenantReaderKey];
  return reader ? reader() : null;
}

export async function getRequestDesignOverride(): Promise<string | null> {
  if (typeof window !== 'undefined') return null;
  const reader = runtime[designOverrideReaderKey];
  return reader ? reader() : null;
}

/** orkhan.art's own one-of-one design (White Swan) and its old alias. No other site wears it. */
const ORKHAN_ONLY_DESIGNS = new Set(['oma-original', 'existing']);

export async function resolveActiveDesignId(
  settings: { designId?: string | null; activeDesignId?: string | null } | null | undefined,
  tenantSlug: string | null | undefined
): Promise<string> {
  const override = await getRequestDesignOverride();
  const chosen =
    override ||
    settings?.designId?.trim() ||
    settings?.activeDesignId?.trim() ||
    defaultDesignForTenant(tenantSlug);
  if (!isOrkhanTenant(tenantSlug) && ORKHAN_ONLY_DESIGNS.has(chosen.toLowerCase())) {
    return defaultDesignForTenant(tenantSlug);
  }
  return chosen;
}

export const currentTenant = getRequestTenant;

/**
 * Names the client on a server-side request that goes to the CMS or the analytics API by itself
 * rather than through this client, orkhan.art included: an unnamed CMS read answers with every
 * client's records. Empty in the browser, where the proxy adds the header to same-origin requests.
 */
export async function clientTenantHeaders(): Promise<Record<string, string>> {
  const tenant = await getRequestTenant();
  return tenant ? { 'x-tenant': tenant } : {};
}

/** `init` with the client's x-tenant added (see clientTenantHeaders); as given on orkhan.art and in the browser. */
export async function withClientTenant(init?: RequestInit): Promise<RequestInit | undefined> {
  const tenant = (await clientTenantHeaders())['x-tenant'];
  if (!tenant) return init;
  const headers = new Headers(init?.headers);
  headers.set('x-tenant', tenant);
  return { ...init, headers };
}

/**
 * Include the tenant in a direct analytics URL, also separating intermediary cache keys.
 * Server reads must target the analytics service, never another artist's site rewrite.
 */
export function withTenantQuery(url: string, tenant: string | null | undefined): string {
  if (!tenant) return url;
  return `${url}${url.includes('?') ? '&' : '?'}tenant=${encodeURIComponent(tenant)}`;
}

/**
 * Server-side CMS reads are kept for 60 seconds, the time pages already ask for. Pages pass
 * `next: { revalidate: 60 }` to the client, but payload-rest-client sends it to the CMS as a query
 * parameter and never to fetch, so every page made a dozen or more uncached CMS calls per visit.
 * Only plain GETs signed with the site's own API key are kept: a request carrying a visitor's
 * cookie or token, or its own cache setting, always goes to the CMS. Next keys the cache on the
 * request headers too, so tenants stay apart.
 */
function isCachedRead(init: RequestInit | undefined, headers: Headers) {
  if (typeof window !== 'undefined' || init?.cache) return false;
  const method = (init?.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') return false;
  if (headers.has('cookie')) return false;
  const auth = headers.get('authorization');
  return !auth || auth.startsWith('users API-Key ');
}

/**
 * A `_t` query parameter that keeps browser reads through /api/proxy from being answered by an old
 * copy. On the server it made every CMS URL unique, so Next's data cache never held catalogue,
 * press or events queries: a collection's 33 artworks took the CMS 1 to 1.2 s on every visit.
 */
export const browserCacheBuster = (): { _t?: number } => (typeof window === 'undefined' ? {} : { _t: Date.now() });

export function createApiClient(apiUrl: string, options: { debug?: boolean } = {}, headers?: Record<string, string>) {
  return createClient<Config, Locale>({
    apiUrl,
    debug: options.debug,
    headers,
    customFetchFn: async (input, init) => {
      const next = new Headers(init?.headers);
      const tenant = await getRequestTenant();
      if (tenant) next.set('x-tenant', tenant);
      return fetch(input, { ...init, headers: next, ...(isCachedRead(init, next) ? { next: { revalidate: 60 } } : {}) });
    },
  });
}

// Singleton API istemcisi
let apiClient: ReturnType<typeof createClient<Config, Locale>> | null = null;

// API istemcisini döndüren fonksiyon
export function getApiClient() {
  if (!apiClient) {
    // API URL'sini oluştur
    let apiUrl: string | null = null;

    if (typeof window !== 'undefined') {
      // Client-side'da next.js proxy'sini kullanın
      apiUrl = '/api/proxy';
    } else if (process.env.NEXT_PUBLIC_PAYLOAD_API_URL) {
      // Server-side'da doğrudan PayloadCMS'ye bağlanın
      apiUrl = `${process.env.NEXT_PUBLIC_PAYLOAD_API_URL.replace(/\/+$/, '')}/api`;
    } else if (process.env.NODE_ENV === 'development') {
      // Yerel geliştirirken opsiyonel localhost fallback
      apiUrl = 'http://localhost:3030/api';
    } else {
      // In production, prefer explicit configuration rather than silent localhost default
      apiUrl = null;
    }
    // if (process.env.NEXT_PUBLIC_PAYLOAD_API_URL) {
    //   apiUrl = `${process.env.NEXT_PUBLIC_PAYLOAD_API_URL}/api`;
    // } else if (typeof window !== 'undefined') {
    //   apiUrl = `https://cave.orkhan.art/api`;
    // }

    // The credential is server-only. In the browser the request goes to
    // /api/proxy, whose route handler attaches the key on the server — sending
    // it from here would mean inlining it into the client bundle, where it was
    // publicly readable until this was changed.
    const isServer = typeof window === 'undefined';
    const headers = isServer ? { Authorization: `users API-Key ${process.env.PAYLOAD_API_KEY ?? ''}` } : undefined;

    apiClient = createApiClient(
      // If apiUrl is null, createClient will still be called with empty string which may error
      // but we prefer to require explicit config in production. Consumers should
      // check reachability before calling server APIs.
      apiUrl ?? '',
      { debug: false },
      headers
    );
  }
  return apiClient;
}
